import { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getAIAdminOverview } from '@/lib/api';

interface CostSummary {
    total_usd: number;
    by_provider_usd: Record<string, number>;
}

interface AIAdminOverview {
    costs: {
        daily: CostSummary;
        weekly: CostSummary;
        monthly: CostSummary;
    };
    fallbacks: Array<{
        id: number;
        provider: string;
        model: string;
        key_suffix: string;
        reason: string;
        created_at: string;
        user_id?: number | null;
    }>;
    generated_at: string;
}

const formatUSD = (value: number) => `$${value.toFixed(4)}`;
const hiddenSystemTextPattern = /DeepSeek(?:\s*V\d+(?:\s*Flash)?)?|Qwen[\w:.-]*|OpenRouter|SiliconFlow|DeepInfra|Cloudflare|ClaudeFlare|\bproviders?\b|\bmodels?\b|\bAPI\b/gi;
const sanitizeDisplayText = (value?: string | null) => String(value || '').replace(hiddenSystemTextPattern, '確認機能');

export function AIAdmin() {
    const [data, setData] = useState<AIAdminOverview | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchOverview = async () => {
        setIsLoading(true);
        try {
            const res = await getAIAdminOverview();
            setData(res.data);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchOverview();
    }, []);

    if (isLoading) {
        return (
            <div className="p-8 min-h-screen bg-[#0c0a09] text-white flex items-center justify-center">
                <Loader2 className="animate-spin" />
            </div>
        );
    }

    const costs = data?.costs;

    return (
        <div className="p-8 min-h-screen bg-[#0c0a09] text-white font-sans">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-serif text-[#d4af37]">AI 管理</h1>
                    <p className="text-gray-500 text-sm italic">Usage Monitor</p>
                </div>
                <Button onClick={fetchOverview} variant="outline" className="border-[#d4af37] text-[#d4af37] gap-2">
                    <RefreshCw size={14} />
                    Refresh
                </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-10">
                {(['daily', 'weekly', 'monthly'] as const).map((key) => {
                    const summary = costs?.[key];
                    return (
                        <div key={key} className="border border-[#d4af37]/30 rounded-lg p-4 bg-[#1c1917]/60">
                            <div className="text-xs uppercase tracking-widest text-[#d4af37]/70">
                                {key === 'daily' ? 'Daily' : key === 'weekly' ? 'Weekly' : 'Monthly'}
                            </div>
                            <div className="text-2xl font-semibold mt-2">
                                {summary ? formatUSD(summary.total_usd) : '$0.0000'}
                            </div>
                            <div className="mt-3 space-y-1 text-xs text-gray-400">
                                {summary && Object.keys(summary.by_provider_usd).length > 0 ? (
                                    Object.entries(summary.by_provider_usd).map(([provider, amount]) => (
                                        <div key={provider} className="flex justify-between">
                                            <span>確認機能</span>
                                            <span>{formatUSD(amount)}</span>
                                        </div>
                                    ))
                                ) : (
                                    <div>内訳なし</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="border border-[#d4af37]/30 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-[#1c1917]">
                    <div className="text-sm font-semibold text-[#d4af37]">フォールバック履歴</div>
                    <div className="text-xs text-gray-500">
                        {data?.generated_at ? new Date(data.generated_at).toLocaleString('ja-JP') : ''}
                    </div>
                </div>
                <table className="w-full text-xs text-left">
                    <thead className="text-[11px] uppercase bg-[#0f0f0f] text-[#d4af37] border-b border-[#d4af37]/30">
                        <tr>
                            <th className="px-3 py-2">Time</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.fallbacks?.length ? (
                            data.fallbacks.map((row) => (
                                <tr key={row.id} className="border-b border-[#d4af37]/10 hover:bg-[#d4af37]/5">
                                    <td className="px-3 py-2 text-gray-400">
                                        {new Date(row.created_at).toLocaleString('ja-JP')}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="text-[#fcd34d] font-semibold">非公開</div>
                                        <div className="text-[10px] text-gray-500">確認機能</div>
                                    </td>
                                    <td className="px-3 py-2 text-gray-300 break-words">{sanitizeDisplayText(row.reason)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={3} className="px-3 py-6 text-center text-gray-500">
                                    フォールバック履歴はまだありません。
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
